import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { weCheckPalette } from '@/utils/palette/weCheckPalette'

export const ChipVariant = {
  Default: 'default',
  Blue: 'blue',
  Green: 'green',
  PrimaryBlue: 'primaryBlue',
  Yellow: 'yellow',
  Red: 'red',
  Disabled: 'disabled',
  Dashed: 'dashed',
  Transparent: 'transparent',
  SelectedTopic: 'selected-topic',
  SelectableTopic: 'selectable-topic',
} as const

export type ChipVariant = (typeof ChipVariant)[keyof typeof ChipVariant]

export const ChipSize = {
  Small: 'small',
  Medium: 'medium',
  Large: 'large',
  Tag: 'tag',
} as const

export type ChipSize = (typeof ChipSize)[keyof typeof ChipSize]

interface ChipProps {
  variant?: ChipVariant
  size?: ChipSize
  text?: string
  icon?: ReactNode
  selected?: boolean
  onClick?: () => void
  className?: string
}

const colorMap: Record<
  ChipVariant,
  { bgColor: string; textColor: string; border: string }
> = {
  default: {
    bgColor: '',
    textColor: '',
    border: '',
  },
  blue: {
    bgColor: '#C6F5FF',
    textColor: '#037797',
    border: 'transparent',
  },
  green: {
    bgColor: '#C8FFD4',
    textColor: '#1E924D',
    border: 'transparent',
  },
  primaryBlue: {
    bgColor: '#087FFF',
    textColor: '#F5F5F5',
    border: '#087FFF',
  },
  yellow: {
    bgColor: '#FFFDCB',
    textColor: '#DFB014',
    border: 'transparent',
  },
  red: {
    bgColor: weCheckPalette.alertRedBase,
    textColor: weCheckPalette.dynamicRed,
    border: weCheckPalette.alertRedBase,
  },
  disabled: {
    bgColor: 'neutral-200',
    textColor: 'neutral-400',
    border: 'transparent',
  },
  dashed: {
    bgColor: 'transparent',
    textColor: weCheckPalette.neutralColor60,
    border: weCheckPalette.neutralColor60,
  },
  transparent: {
    bgColor: weCheckPalette.baseColorHighlight,
    textColor: weCheckPalette.neutralColor,
    border: weCheckPalette.neutralColor60,
  },
  'selected-topic': {
    bgColor: weCheckPalette.alertRedBase,
    textColor: weCheckPalette.alertRedSecondary,
    border: 'transparent',
  },
  'selectable-topic': {
    bgColor: 'transparent',
    textColor: weCheckPalette.neutralColor,
    border: weCheckPalette.neutralColor30,
  },
}

const sizeStyles: Record<ChipSize, string> = {
  small: 'h-5 px-1 py-0.5 rounded text-xs font-medium',
  medium: 'h-[35px] px-4 py-2 rounded-[200px] text-xs font-medium',
  large: 'px-6 py-3 rounded-[200px] text-base font-bold',
  tag: 'h-6 px-3 py-1 rounded-[200px] text-xs font-medium',
}

const SelectionIndicator = ({ selected }: { selected: boolean }) =>
  selected ? (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="8" cy="8" r="8" fill={weCheckPalette.primary} />
      <path
        d="M5 8L7 10L11 6"
        stroke={weCheckPalette.staticWhite}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ) : (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="8" cy="8" r="7.5" stroke="currentColor" strokeWidth="1" />
    </svg>
  )

export const Chip = ({
  variant = ChipVariant.Default,
  size = ChipSize.Small,
  text,
  icon,
  selected = false,
  onClick,
  className = '',
}: ChipProps) => {
  const { bgColor, textColor, border } = colorMap[variant]
  const isDashed = variant === ChipVariant.Dashed
  const isSelectable = variant === ChipVariant.SelectableTopic
  const isInteractive = isSelectable || Boolean(onClick)

  const sharedClassName = cn(
    'inline-flex items-center justify-center gap-1 leading-none whitespace-nowrap',
    sizeStyles[size],
    isInteractive && 'cursor-pointer transition-colors',
    className,
  )

  const inlineStyle = {
    backgroundColor: bgColor,
    border: `0.5px ${isDashed ? 'dashed' : 'solid'} ${border}`,
    color: textColor,
  }

  const content = (
    <>
      {text}
      {isSelectable && <SelectionIndicator selected={selected} />}
      {icon}
    </>
  )

  if (isInteractive) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={sharedClassName}
        style={inlineStyle}
      >
        {content}
      </button>
    )
  }

  return (
    <div className={sharedClassName} style={inlineStyle}>
      {content}
    </div>
  )
}
